import {
  ArrowLeft,
  Barcode,
  Bot,
  Building2,
  Calculator,
  FileText,
  FlaskConical,
  FolderOpen,
  GalleryHorizontalEnd,
  Handshake,
  IdCard,
  Inbox,
  LayoutList,
  LayoutTemplate,
  ListChecks,
  ListOrdered,
  Mail,
  Mails,
  MessageSquare,
  Microscope,
  Phone,
  Plug,
  RefreshCw,
  ShieldCheck,
  SquareTerminal,
  Target,
  Timer,
  TrendingUp,
  Truck,
  UserCog,
  Users,
  Wrench,
} from 'lucide-react'

import {
  bookkeepingPitch,
  type DrawerPage,
  PRODUCT_TAB_ENTITY,
  PRODUCT_TAB_LABEL,
  type ProductBlankStateProps,
  type ProductPitch,
  ProductTab,
  type SidebarIcon,
  type SidebarLink,
  vaultPitch,
} from '@/components/sidebar'
import { ADMIN_BASE_URL, adminHref, bookkeeperHref, isProduction, TEMPORAL_URL } from '@/config/config'
import { DEFAULT_SIDEBAR_TAB, DEFAULT_STAFF_SIDEBAR_TAB, SidebarTab } from '@/lib/sidebarTab'
import { OrgStatus, type SidebarNav, type SidebarNavItem, type User } from '@/models/user'

export const SIDEBAR_TAB_LABEL: Record<SidebarTab, string> = {
  [SidebarTab.Insurance]: PRODUCT_TAB_LABEL[ProductTab.Insurance],
  [SidebarTab.Compliance]: PRODUCT_TAB_LABEL[ProductTab.Compliance],
  [SidebarTab.Finance]: 'Bookkeeper',
  [SidebarTab.Console]: 'Console',
}

export const SIDEBAR_TAB_ENTITY: Partial<Record<SidebarTab, string>> = {
  [SidebarTab.Insurance]: PRODUCT_TAB_ENTITY[ProductTab.Insurance],
  [SidebarTab.Compliance]: PRODUCT_TAB_ENTITY[ProductTab.Compliance],
  [SidebarTab.Finance]: PRODUCT_TAB_ENTITY[ProductTab.Finance],
}

/**
 * Tags a hand-off to Bookkeeper so it can attribute a sign-up back to this nav. Only the
 * pitches carry it — the rows are navigation for someone who has already arrived, not a referral.
 */
const financeCta = (path: string) => bookkeeperHref(`${path}?utm_source=insure`)

/** Only shown to someone with no bookkeeping account, so it starts with signing up for one. */
export const FINANCE_PITCHES: ProductPitch[] = [bookkeepingPitch(financeCta('/bookkeeper/start'))]

/** The Bookkeeper tab's page for a fleet with no bookkeeping account. */
export const BOOKKEEPER_BLANK_STATE: ProductBlankStateProps = {
  title: 'Bookkeeping',
  heading: 'See where your fleet’s money is really going',
  lines: ['Catch up on the last few months available'],
  cta: { label: 'Set up now', href: financeCta('/bookkeeper/start') },
  learnMore: { label: 'Learn more about Cai bookkeeping', href: 'https://www.cai.ai/bookkeeper' },
}

/** The Insurance tab's page for an account with no insurance fleet, such as a Bookkeeper-only customer. */
export const INSURANCE_BLANK_STATE: ProductBlankStateProps = {
  title: 'Insurance',
  heading: 'Actually get to shop multiple quotes this year',
  lines: ['Quotes from the carriers that write for fleets your size, side by side.'],
  cta: { label: 'Get your quotes', href: '/insurance/start' },
  learnMore: { label: 'Learn more about Cai insurance', href: 'https://www.cai.ai/insurance' },
}

const FORCED_OFFERS: Record<string, SidebarTab> = {
  insurance: SidebarTab.Insurance,
  bookkeeper: SidebarTab.Finance,
}

/** `?offer=insurance` or `?offer=bookkeeper` shows that tab's blank state outside production, for previewing it. */
function forcedOffer(): SidebarTab | null {
  if (isProduction) return null
  return FORCED_OFFERS[new URLSearchParams(window.location.search).get('offer') ?? ''] ?? null
}

/** A tab the account has not bought, whose page is an offer rather than the current route. */
export function isProductOffer(
  tab: SidebarTab | null,
  user: User,
  pathname: string,
  picked = false
): boolean {
  // Account-wide pages belong to no product, so a tab carried onto one never covers it; a fresh pick does.
  if (!picked && ACCOUNT_PATHS.some((path) => isUnder(pathname, path))) return false
  if (tab && tab === forcedOffer()) return true
  if (tab === SidebarTab.Finance) return user.sidebar.finance.length === 0
  // Staff with no customer switched in have no rows either; they are not being sold insurance.
  if (tab === SidebarTab.Insurance) return user.sidebar.insurance.length === 0 && !user.isCaiAdmin
  return false
}

/** What a tab offers an account that holds none of it. Insurance is never pitched here. */
export function emptyTabPitches(tab: SidebarTab, user: User): ProductPitch[] {
  if (tab === SidebarTab.Compliance) {
    // A fleet that is not active yet cannot set the vault up, so it gets the card with no way in.
    return [vaultPitch(user.currentOrg?.status === OrgStatus.ACTIVE ? '/insure/vault' : undefined)]
  }
  if (tab === SidebarTab.Finance) return FINANCE_PITCHES
  return []
}

export const CONSOLE_ITEMS: SidebarLink[] = [
  { title: 'Customers', url: '/insure/internal/customers', icon: Users },
  { title: 'Applications', url: '/insure/internal/applications', icon: GalleryHorizontalEnd },
  { title: 'Claims', url: '/insure/internal/claims', icon: FolderOpen },
  { title: 'Requotes', url: '/insure/internal/requotes', icon: RefreshCw },
  { title: 'Endorsements', url: '/insure/internal/endorsements', icon: ListChecks },
  { title: 'Pending Changes', url: '/insure/internal/pending-equipment-changes', icon: Truck },
  { title: 'COI Requests', url: '/insure/internal/coi-requests', icon: ShieldCheck },
]

export const SALES_ITEMS: SidebarLink[] = [
  { title: 'Sales Leads', url: '/insure/internal/sales-leads', icon: Handshake },
  { title: 'Carriers', url: '/insure/internal/carriers', icon: Building2 },
  { title: 'My Queue', url: '/insure/internal/work-queue', icon: Inbox },
  { title: 'My Pipeline', url: '/insure/internal/my-pipeline', icon: Target },
  { title: 'Email Templates', url: '/insure/internal/email-templates', icon: LayoutTemplate },
  { title: 'Sequence Templates', url: '/insure/internal/sequence-templates', icon: LayoutList },
  { title: 'Sequences', url: '/insure/internal/sequences', icon: ListOrdered },
  { title: 'Insurance Estimator', url: '/insure/internal/sales/insurance-estimator', icon: Calculator },
]

export const ADMIN_ITEMS: SidebarLink[] = [
  { title: 'Staff Users', url: '/insure/internal/staff-users', icon: UserCog },
  { title: 'Agent Tools', url: '/insure/internal/agent-tools', icon: Bot },
  { title: 'Internal Agent Chat', url: '/insure/internal/agent-chat', icon: MessageSquare },
  { title: 'COI Generator', url: '/insure/internal/coi-generator', icon: Microscope },
  { title: 'QA', url: '/insure/internal/qa', icon: FlaskConical },
  { title: 'Corgi Connection', url: '/insure/internal/corgi-connection', icon: Plug },
]

export const LOOKUP_ITEMS: SidebarLink[] = [
  { title: 'CAB', url: '/insure/internal/cab', icon: IdCard },
  { title: 'VIN', url: '/insure/internal/vin', icon: Barcode },
]

export const REPORTING_ITEMS: SidebarLink[] = [
  { title: 'Sales', url: '/insure/internal/sales-analytics', icon: TrendingUp },
  { title: 'Quotes', url: '/insure/internal/quotes-analytics', icon: FileText },
  { title: 'Agents', url: '/insure/internal/agent-runs', icon: Bot },
  { title: 'Email', url: '/insure/internal/email-reporting', icon: Mail },
  { title: 'All Emails', url: '/insure/internal/emails', icon: Mails },
  { title: 'SMS', url: '/insure/internal/sms', icon: MessageSquare },
  { title: 'Calls', url: '/insure/internal/calls', icon: Phone },
]

export const EXTERNAL_ITEMS: SidebarLink[] = [
  { title: 'Django Admin', url: adminHref(`${ADMIN_BASE_URL}/`), icon: Wrench },
  { title: 'Temporal', url: TEMPORAL_URL, icon: Timer },
  {
    title: 'Terminal',
    url: `https://dashboard${isProduction ? '' : '.sandbox'}.withterminal.com/`,
    icon: SquareTerminal,
  },
]

/** A row that swaps the whole sidebar rather than navigating anywhere. */
export interface SwitchRow {
  title: string
  icon: SidebarIcon
  tab: SidebarTab
}

export const CONSOLE_SWITCH: SwitchRow = { title: 'Console', icon: SquareTerminal, tab: SidebarTab.Console }

export const PRODUCTS_SWITCH: SwitchRow = {
  title: 'Insurance sidebar',
  icon: ArrowLeft,
  tab: SidebarTab.Insurance,
}

const CONSOLE_PATH = '/insure/internal'
const ACCOUNT_PATHS = ['/insure/admin-settings']
const VAULT_PATH = '/insure/vault'

function isUnder(pathname: string, url: string): boolean {
  return pathname === url || pathname.startsWith(`${url}/`)
}

/** A console page: the staff tools under /insure/internal. */
export function isConsolePath(pathname: string): boolean {
  return isUnder(pathname, CONSOLE_PATH)
}

export function isItemActive(pathname: string, item: Pick<SidebarLink, 'url' | 'exact'>): boolean {
  if (item.exact) return pathname === item.url
  return isUnder(pathname, item.url)
}

/**
 * Which tab owns a URL, so the nav opens on the section the customer is looking at.
 * Null for pages that belong to no tab (Settings), which keep the tab they were last on.
 */
export function tabForPathname(pathname: string, nav: SidebarNav): SidebarTab | null {
  if (isConsolePath(pathname)) return SidebarTab.Console
  // Static rather than read off the rows: a customer being pitched the vault has none,
  // and should still land on Compliance when they open a vault URL.
  if (isUnder(pathname, VAULT_PATH)) return SidebarTab.Compliance
  if (nav.insurance.some((item) => isUnder(pathname, item.url))) return SidebarTab.Insurance
  return null
}

export function isTabVisible(tab: SidebarTab, user: User): boolean {
  // Compliance is hidden for now; vault URLs fall back to Insurance.
  if (tab === SidebarTab.Compliance) return false
  // The console is not a product.
  return tab !== SidebarTab.Console || user.isCaiAdmin
}

/** The tab strip. Console is not in it — four tabs do not fit, so staff switch into it from a row. */
export function productTabs(user: User): SidebarTab[] {
  return [SidebarTab.Insurance, SidebarTab.Compliance, SidebarTab.Finance].filter((tab) =>
    isTabVisible(tab, user)
  )
}

export function resolveSidebarTab(
  user: User,
  pathname: string,
  picked: SidebarTab | null,
  stored: SidebarTab | null
): SidebarTab {
  const fallback = user.isCaiAdmin ? DEFAULT_STAFF_SIDEBAR_TAB : DEFAULT_SIDEBAR_TAB
  const requested = picked ?? tabForPathname(pathname, user.sidebar) ?? stored ?? fallback
  if (isTabVisible(requested, user)) return requested
  return productTabs(user)[0] ?? DEFAULT_SIDEBAR_TAB
}

/** Insurance's own page stands apart, and each heading the rail shows starts a new run in the menu. */
export function insuranceDrawerPages(rows: SidebarNavItem[]): DrawerPage[] {
  return rows.map((row, index) => ({
    ...row,
    ruleBefore: index === 1 || (index > 1 && row.group !== rows[index - 1].group),
  }))
}
